import * as singleSpa from "single-spa"
import { APP_URL } from "../constants/app-url"
import { coreActions } from "../slices/core.slice"

export function initAIPanel(store) {
  const storeData = store.getState()
  const userToken = storeData.userToken
  // Hit the AI-services host (*.forcepoint.io) that serves ai-settings, the
  // same host the aria-ui bundle is fetched from. Falls back to the platform
  // origin for localhost / unrecognised hosts.
  const aiServicesBaseUrl = resolveAiServicesBaseUrl()
  const url =
    (aiServicesBaseUrl || APP_URL) + "/ai/api/tenants/current/ai-settings"
  fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${userToken}`
    }
  })
    .then((res) => res.json())
    .then((res) => {
      const isAiEnabled = res["ai.enabled"]
      store.dispatch(coreActions.setIsAIEnabled(isAiEnabled))
      if (isAiEnabled) {
        registerAIPanelUI()
      }
    })
    .catch((err) => {
      console.error("Error fetching AI settings: ", err)
    })
}

/**
 * Maps the current platform host to the AI-services host (*.forcepoint.io)
 * that serves both the aria-ui bundle and the AI APIs (ai-settings, ai-chat).
 *
 *   fpmumbai.dev.forcepointone.com   → https://fpmumbai.dscdev.forcepoint.io
 *   fpmumbai.qa.forcepointone.com    → https://fpmumbai.dscqa.forcepoint.io
 *   fpmumbai.trial.forcepointone.com → https://fpmumbai.dsctrial.forcepoint.io
 *   fpmumbai.forcepointone.com       → https://fpmumbai.app.forcepoint.io
 *
 * Returns the origin only (no trailing path) so callers can append their own
 * path. Returns null for unrecognised hosts so callers fall back to the
 * platform origin / static SystemJS import map (which has the correct
 * //localhost:6001/… entry for local dev).
 *
 * Exported for micro-frontends that call an AI service directly — e.g. tenantui
 * reads and writes the per-tenant ai-chat configuration. Keep it the single
 * source of truth for this mapping rather than re-deriving the host locally.
 *
 * @param tenantHost Host label of the tenant being acted on, replacing the one
 * in the current URL. Tenants are region-based and each region has its own
 * AI-services deployment and database, so a console that administers *other*
 * tenants — the super-admin console, whose own host resolves to a single region
 * — MUST pass the target tenant's host. Omit it when acting on the tenant the
 * browser is already on (the aria-ui / ai-settings case).
 */
export const resolveAiServicesBaseUrl = (tenantHost?: string): string | null => {
  if (typeof window === "undefined") return null
  const host = window.location.hostname.includes("localhost")
  ? new URL(APP_URL as string).hostname
  : window.location.hostname
  const protocol = "https:"
  const dot = host.indexOf(".")
  if (dot <= 0) return null

  const tenant = tenantHost?.trim() || host.slice(0, dot)
  const suffix = host.slice(dot + 1)

  const SUFFIX_MAP: Array<{ from: string; to: string }> = [
    { from: "dev.forcepointone.com", to: "dscdev.forcepoint.io" },
    { from: "qa.forcepointone.com", to: "dscqa.forcepoint.io" },
    { from: "trial.forcepointone.com", to: "dsctrial.forcepoint.io" },
    { from: "forcepointone.com", to: "app.forcepoint.io" },
    // Tenants already hosted on the AI-services domain (e.g.
    // mcpdev1.dscdev.forcepoint.io) map to themselves — the .com entries
    // above only match when browsing from the .forcepointone.com side.
    { from: "dscdev.forcepoint.io", to: "dscdev.forcepoint.io" },
    { from: "dscqa.forcepoint.io", to: "dscqa.forcepoint.io" },
    { from: "dsctrial.forcepoint.io", to: "dsctrial.forcepoint.io" },
    { from: "app.forcepoint.io", to: "app.forcepoint.io" }
  ]
  const match = SUFFIX_MAP.find((m) => suffix === m.from)
  if (!match) return null

  return `${protocol}//${tenant}.${match.to}`
}

/**
 * URL of the aria-ui bundle on the AI-services host
 * (`…/forcepoint-platform-aria-ui.js`). Returns null for localhost /
 * unrecognised hosts so the caller falls back to the static SystemJS import
 * map key.
 */
const resolveAriaUiBundleUrl = (): string | null => {
  if (window.location.hostname.includes("localhost")) return null
  const base = resolveAiServicesBaseUrl()
  return base ? `${base}/forcepoint-platform-aria-ui.js` : null
}

export const registerAIPanelUI = () => {
  // Prefer fetching the aria-ui bundle from the AI services host (e.g.
  // *.dscdev.forcepoint.io/aria-ui/…) so the JS lives alongside the AI
  // backend that serves it. Falls back to the SystemJS import-map key for
  // localhost / unrecognised hosts — the local importmap entry already
  // points at //localhost:6001 for dev.
  const ariaBundleUrl = resolveAriaUiBundleUrl()
  singleSpa.registerApplication({
    name: "@forcepoint/platform-aria-ui",
    app: () => {
      // BUG #7: purge SystemJS's cached failure and rethrow so single-spa
      // marks LOAD_ERROR and retries on the next routing event. An
      // empty-lifecycle fallback would be cached as "loaded" and disable
      // the AI panel for the whole session after one transient failure.
      const specifier = ariaBundleUrl || "@forcepoint/platform-aria-ui"
      return (System.import(specifier) as Promise<
        singleSpa.LifeCycles<any>
      >).catch((error) => {
        console.error(
          "[registerAIPanelUI] Failed to load aria-ui from",
          specifier,
          ":",
          error
        )
        try {
          const sys = System as any
          sys.delete?.(sys.resolve(specifier))
        } catch {
          // nothing to purge
        }
        throw error
      })
    },
    activeWhen: "/"
  })
}

/**
 * Unregister the aria-ui single-spa app and return the promise that resolves
 * when its unmount lifecycle has completed. Idempotent — safe to call when
 * the app was never registered (e.g. a tenant where `ai.enabled` is false,
 * so `registerAIPanelUI` never ran). Callers that need to know the unmount
 * has finished (e.g. `appLogout` before redirecting) should await the
 * returned promise; callers that don't care can fire-and-forget.
 */
export const unregisterAIPanelUI = (): Promise<void> => {
  const APP_NAME = "@forcepoint/platform-aria-ui"
  if (!singleSpa.getAppNames().includes(APP_NAME)) {
    return Promise.resolve()
  }
  return singleSpa.unregisterApplication(APP_NAME)
}
